/**
 * OpenRouter paid with Genie credits, through api-app's
 * `POST /api/ai/text/generate/stream` (session cookie, `credentials: 'include'`).
 *
 * The route charges the wallet before it streams, and only knows single-turn
 * requests: `{ prompt, model, systemPrompt, parameters }` with the prompt at
 * most 8000 characters and the system prompt at most 4000. A conversation
 * (the repair round) is folded into the one prompt.
 *
 * `model` is resolved twice on the server, and both lookups accept the same two
 * forms: the catalogue's internal `ai_models.id` (cuid) or its `endpointId`.
 * Billing (`walletService.findPricingByModel`) matches `api_pricing.model`,
 * then `ai_models.endpointId`, then `ai_models.id`; api-ai
 * (`ModelRegistryAdapter.getFullModel`) matches `id`, then an enabled row by
 * `endpointId`, requires the provider to be `openrouter`, and sends the
 * `endpointId` to OpenRouter. For OpenRouter rows the endpointId IS the
 * OpenRouter slug, so a slug such as `anthropic/claude-sonnet-4` works exactly
 * when Genie's catalogue has it enabled and priced. An unpriced slug fails
 * before spending (404 `PRICING_NOT_FOUND` from billing). api-ai waits for the
 * first token before it sends its 200, so a priced model it then refuses
 * (disabled, not OpenRouter, OpenRouter 4xx/5xx, no output) is an HTTP error
 * that api-app refunds. Only a failure after the first token is paid for and
 * arrives as an `error` event. The credits picker offers only enabled
 * OpenRouter catalogue rows.
 *
 * `parameters.temperature` / `parameters.max_tokens` are forwarded to
 * OpenRouter (api-ai defaults them to 0.7 / 2000 otherwise).
 *
 * The stream is Genie's own SSE: `data: {"type":"token","content":"..."}`,
 * `{"type":"progress","progress":n}`, `{"type":"done","variant":{...}}`, or
 * `{"type":"error","message":"..."}` once headers are sent.
 *
 * The `done` variant carries OpenRouter's `finishReason`, honoured like the
 * direct transports do (`length` means the answer was cut off); a mid-stream
 * error chunk arrives as an `error` event.
 */

import type { ShaderChatMessage } from '../types'
import { ShaderGenerationError } from '../types'
import { abortedError, readErrorBody, toShaderError } from './errors'
import type { ChatTransportRequest } from './openai-compatible'
import { readSseData, truncatedError, unfinishedStreamError } from './sse'

export const GENIE_CREDITS_PROMPT_LIMIT = 8000
export const GENIE_CREDITS_SYSTEM_PROMPT_LIMIT = 4000

function apiBaseUrl(): string {
  // Read lazily: outside Vite (tests, scripts) import.meta.env may be absent.
  const env = (import.meta as { env?: Record<string, string | undefined> }).env
  return env?.VITE_API_BASE_URL ?? ''
}

const TOO_LONG =
  'This shader is too long to edit with Genie credits (the prompt limit is 8000 characters). Shorten the request or use a local model.'

/** One prompt from a conversation; the latest turn alone when the transcript does not fit. */
export function flattenForCredits(messages: ShaderChatMessage[]): string {
  if (messages.length === 1) return messages[0].content
  const transcript = messages.map((m) => `${m.role === 'user' ? 'USER' : 'ASSISTANT'}:\n${m.content}`).join('\n\n')
  if (transcript.length <= GENIE_CREDITS_PROMPT_LIMIT) return transcript
  const lastUser = [...messages].reverse().find((m) => m.role === 'user')
  return lastUser?.content ?? transcript
}

async function creditsHttpError(response: Response): Promise<ShaderGenerationError> {
  const body = await readErrorBody(response)
  const status = response.status
  if (status === 402 || body.code === 'INSUFFICIENT_BALANCE') {
    return new ShaderGenerationError('Not enough Genie credits for this model. Top up, or choose a local model.', 'INSUFFICIENT_BALANCE')
  }
  if (status === 404 && body.code === 'PRICING_NOT_FOUND') {
    return new ShaderGenerationError('This model is not available with Genie credits. Pick an available Magic Lamp model.', 'PRICING_NOT_FOUND')
  }
  if (status === 401 || status === 403) return new ShaderGenerationError('Sign in to Genie to use Genie credits.', 'AUTH')
  if (status === 429) return new ShaderGenerationError('Too many requests. Wait a moment and try again.', 'RATE_LIMITED')
  return new ShaderGenerationError(body.message ? `Genie: ${body.message}` : `Genie returned an error (HTTP ${status}).`, 'UNKNOWN')
}

interface CreditsEvent {
  type?: string
  content?: unknown
  message?: unknown
  /** OpenRouter's finish reason, forwarded by api-ai. */
  variant?: { finishReason?: unknown; finish_reason?: unknown }
}

function finishReasonOf(event: CreditsEvent): unknown {
  return event.variant?.finishReason ?? event.variant?.finish_reason
}

export async function completeGenieCredits(request: ChatTransportRequest): Promise<string> {
  const { signal } = request
  if (signal.aborted) throw abortedError()
  const prompt = flattenForCredits(request.messages)
  if (prompt.length > GENIE_CREDITS_PROMPT_LIMIT) throw new ShaderGenerationError(TOO_LONG, 'UNKNOWN')
  if (request.system.length > GENIE_CREDITS_SYSTEM_PROMPT_LIMIT) {
    throw new ShaderGenerationError('The shader instructions are too long for Genie credits. Use your own key or a Local model.', 'UNKNOWN')
  }

  try {
    const response = await fetch(`${apiBaseUrl()}/api/ai/text/generate/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        prompt,
        model: request.model,
        systemPrompt: request.system,
        parameters: { temperature: request.temperature, max_tokens: request.maxTokens },
      }),
      signal,
    })
    if (!response.ok) throw await creditsHttpError(response)

    let text = ''
    let finished = false
    await readSseData(response, signal, (data) => {
      let event: CreditsEvent
      try {
        event = JSON.parse(data) as CreditsEvent
      } catch {
        return
      }
      if (event.type === 'token' && typeof event.content === 'string') {
        text += event.content
        request.onText?.(text)
      } else if (event.type === 'done') {
        const reason = finishReasonOf(event)
        if (reason === 'length') throw truncatedError()
        if (reason === 'error') throw new ShaderGenerationError('The model stopped with an error before the shader was finished.', 'UNKNOWN')
        finished = true
        return 'stop'
      } else if (event.type === 'error') {
        throw new ShaderGenerationError(typeof event.message === 'string' ? event.message : 'The model stopped with an error.', 'UNKNOWN')
      }
    })
    if (!finished) throw unfinishedStreamError()
    return text
  } catch (error) {
    throw toShaderError(error, {
      onNetworkError: () => new ShaderGenerationError('Could not reach Genie. Check your connection and try again.', 'NETWORK'),
    })
  }
}
